import { Label } from "@fluentui/react";
import { IFieldConfig } from "components/form/types";
import { useFormikContext } from "formik";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";
import {
  PremiumType,
  TimelineDialogType,
  TimelineSegment,
  TimelineType,
} from "../../../types/types";
import { useFields } from "../../form/useFields";
import FleetLegalFormContent from "../components/FleetLegalFormContent";
import HullOnLandFormContent from "../components/HullOnLandFormContent";
import MachineryBreakdownFormContent from "../components/MachineryBreakdownFormContent";
import { useAnnualNetPremium } from "../hooks/useAnnualNetPremium";
import { useDefaultSettings } from "../hooks/useDefaultSettings";
import { useEqualPaymentMethods } from "../hooks/useEqualPaymentMethods";
import { useInsurerSettings } from "../hooks/useInsurerSettings";
import { TSetDefaultSettingValues } from "./AddPremiumDialog/AddPremiumDialog";
import { addPremiumDialogConfig } from "./addPremiumDialogConfig";
import {
  getOneTimePaymentMethodId,
  isPremiumExpiryDateShown,
} from "./premiumExpiryDate";

interface IProps {
  premiumTypeKey: string;
  item: TimelineSegment;
  setDefaultSettingValues: ({
    setFieldValue,
    values,
  }: TSetDefaultSettingValues) => void;
}

export const AddPremiumDialogContent = ({
  premiumTypeKey,
  item,
  setDefaultSettingValues,
}: IProps) => {
  const vehicle = useSelector((s) => s.vehicle.vehicle);
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const appSettings = useSelector((s) => s.timeline.timelineAppSettings);
  const type = useSelector((s) => s.timeline.dialog.type);

  const isInsertOperation = type === TimelineDialogType.INSERT_PREMIUM;
  const isSwitchOperation = type === TimelineDialogType.SWITCH_HULL_TYPE;

  const config = useMemo(() => {
    if (!appSettings) return;
    const isAustria = ["Country.AT"].includes(appSettings?.COUNTRY_CODE);
    const austriaOnlyRequiredFields = [
      "hullProduct",
      "hullDeductiblePercent",
      "hullDeductibleMinAmount",
      "hullDeductibleComment",
      "gap",
      "replacementValue",
      "grossNegligence",
      "electroPlusPackage",
      "isPolicyIssued",
    ];

    let baseConfig: IFieldConfig = addPremiumDialogConfig;

    if (
      appSettings?.HAS_GREENCARD_EXPIRY_DATE === false ||
      premiumTypeKey !== PremiumType.MTPL
    ) {
      baseConfig = Object.keys(addPremiumDialogConfig).reduce(
        (acc, key) => {
          if (key !== "greenCardExpiryDate") {
            acc[key] = addPremiumDialogConfig[key];
          }
          return acc;
        },
        {} as IFieldConfig,
      );
    }

    if (!isAustria) {
      return Object.keys(baseConfig).reduce((acc, key) => {
        acc[key] = austriaOnlyRequiredFields.includes(key)
          ? { ...baseConfig[key], isRequired: false }
          : baseConfig[key];
        return acc;
      }, {} as IFieldConfig);
    }

    return baseConfig;
  }, [appSettings, premiumTypeKey]);

  const oneTimePaymentMethodId = useSelector((s) =>
    getOneTimePaymentMethodId(s.taxonomy),
  );
  const expiryDateFields = isPremiumExpiryDateShown(
    appSettings?.COUNTRY_CODE,
    (values as any)?.paymentMethod,
    oneTimePaymentMethodId,
  )
    ? [config?.expiryDate]
    : [];

  useEffect(() => {
    const hullIsBasicForm = (values as any)?.hullIsBasic?.value;
    setDefaultSettingValues({
      setFieldValue,
      values,
      noToastMsg: true,
      // for a Hull <-> Hull-Basic conversion keep the insurer of the converted premium
      // instead of falling back to the fleet's main hull insurer
      insurer: isSwitchOperation
        ? (values as any)?.insurerPartnerNumber
        : undefined,
      isHullPremium: !(
        hullIsBasicForm === "true" || hullIsBasicForm === "basic"
      ),
      setFieldTouched,
    });
  }, []);

  useAnnualNetPremium(premiumTypeKey);
  useEqualPaymentMethods(premiumTypeKey);

  useDefaultSettings();
  useInsurerSettings();

  const suspensionDaysIncluded = appSettings?.SUSPENSION_DAYS_INCLUDED;
  const activationDaysIncluded = appSettings?.ACTIVATION_DAYS_INCLUDED;
  const suspensionDaysLimitIncluded =
    appSettings?.SUSPENSION_DAYS_LIMIT_INCLUDED;
  const garageRiskDiscountPercentIncluded =
    appSettings?.GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED;

  let suspensionDaysFields = [];
  if (suspensionDaysIncluded) {
    suspensionDaysFields.push(config.suspensionDays);
  }
  let activationDaysFields = [];

  if (activationDaysIncluded) {
    activationDaysFields.push(config.activationDays);
  }

  let suspensionDaysLimitFields = [];
  if (suspensionDaysLimitIncluded) {
    suspensionDaysLimitFields.push(config.suspensionDaysLimit);
  }

  let garageRiskDiscountPercentFields = [];
  if (garageRiskDiscountPercentIncluded) {
    garageRiskDiscountPercentFields.push(config.garageRiskDiscountPercent);
  }

  let policyStatusFields = [];
  if (
    appSettings?.VEHICLE_TIMELINE_TYPES.includes(
      TimelineType.VEHICLE_POLICY_STATUSES,
    )
  ) {
    policyStatusFields = [config.policyStatus];
  }

  let groupPolicyFields = [];
  if (appSettings?.INCLUDE_GROUP_POLICY) {
    groupPolicyFields = [config.insurerGroupPolicyNumber];
  }
  const { all: mtplOrHullFieldsLeft } = useFields([
    config.dueDate,
    config.insurerPartnerNumber,
    config.insurerBranchOffice,
    ...policyStatusFields,
    config.insurerPolicyNumber,
    ...groupPolicyFields,
    config.isPolicyIssued,
    config.documentDate,
  ]);
  const { all: leftFieldsAdd } = useFields(
    appSettings?.HAS_ANNUAL_PREMIUM_EUR
      ? [
          [config.annualGrossPremium, config.annualGrossPremiumEur],
          [config.annualNetPremium, config.annualNetPremiumEur],
        ]
      : [config.annualGrossPremium, config.annualNetPremium],
  );

  const { all: leftFieldsInsert } = useFields([
    config.annualGrossPremium,
    config.annualNetPremium,
  ]);

  const { all: mtplOrHullFieldsRightAdd } = useFields(
    [
      config.validFromDate,
      ...expiryDateFields,
      ...(config.greenCardExpiryDate ? [config.greenCardExpiryDate] : []),
      ...(appSettings?.DEFAULT_COLUMNS.includes("mtplPolicyStatusComment") ||
      appSettings?.DEFAULT_COLUMNS.includes("hullPolicyStatusComment")
        ? [config.policyStatusComment]
        : []),
      { ...config.paymentMethod, include: appSettings?.PAYMENT_METHODS },
      config.paymentMean,
      ...suspensionDaysFields,
      ...activationDaysFields,
    ],
    undefined,
    setFieldTouched,
  );

  const { all: mtplOrHullFieldsRightInsert } = useFields([
    config.validFromDate,
    config.validToDate,
    ...(config.greenCardExpiryDate ? [config.greenCardExpiryDate] : []),
    config.cancelationDocumentDate,
    appSettings?.HAS_REASON_FOR_CANCELLING_INSURANCE_PREMIUM_FIELD
      ? config.reasonForCancellingInsurance
      : [],
    ...(appSettings?.DEFAULT_COLUMNS.includes("mtplPolicyStatusComment") ||
    appSettings?.DEFAULT_COLUMNS.includes("hullPolicyStatusComment")
      ? [config.policyStatusComment]
      : []),
    { ...config.paymentMethod, include: appSettings?.PAYMENT_METHODS },
    config.paymentMean,
    ...suspensionDaysFields,
    ...activationDaysFields,
  ]);

  // Hull <-> Hull-Basic conversion: same as Add (the cancelation of the converted premium has its own row below)
  const { all: mtplOrHullFieldsRightSwitch } = useFields(
    [
      config.validFromDate,
      ...expiryDateFields,
      ...(appSettings?.DEFAULT_COLUMNS.includes("mtplPolicyStatusComment") ||
      appSettings?.DEFAULT_COLUMNS.includes("hullPolicyStatusComment")
        ? [config.policyStatusComment]
        : []),
      { ...config.paymentMethod, include: appSettings?.PAYMENT_METHODS },
      config.paymentMean,
      ...suspensionDaysFields,
      ...activationDaysFields,
    ],
    undefined,
    setFieldTouched,
  );

  let leftFields = leftFieldsAdd;
  if (isInsertOperation) {
    leftFields = leftFieldsInsert;
  }

  let rightFieldsMtplOrHull = mtplOrHullFieldsRightAdd;
  if (isInsertOperation) {
    rightFieldsMtplOrHull = mtplOrHullFieldsRightInsert;
  }
  if (isSwitchOperation) {
    rightFieldsMtplOrHull = mtplOrHullFieldsRightSwitch;
  }

  const { all: rightFields } = useFields([...suspensionDaysLimitFields]);
  const { all: rightFieldsOtherAdd } = useFields([config.validFromDate]);
  const { all: rightFieldsOtherInsert } = useFields([
    config.validFromDate,
    config.validToDate,
  ]);

  let rightFieldsOther = rightFieldsOtherAdd;
  if (isInsertOperation) {
    rightFieldsOther = rightFieldsOtherInsert;
  }

  const { all: motorTaxWithMtplLeft } = useFields([
    config.isMotorTaxIncluded,
    config.motorTaxExclusionReason,
  ]);
  const { all: motorTaxWithMtplRight } = useFields([
    config.motorTaxPaymentMethod,
    config.motorTaxSuspensionDaysLimit,
  ]);

  const { all: motorTaxWithMtplBottom } = useFields([
    config.motorTaxExclusionComment,
  ]);

  const { all: hullFields1 } = useFields([...garageRiskDiscountPercentFields]);
  // the target hull type of a conversion is fixed by the menu item that opened the dialog
  const { all: hullFields2 } = useFields([
    isSwitchOperation
      ? {
          ...config.hullIsBasic,
          d: (bag) => ({ ...config.hullIsBasic.d?.(bag), isDisabled: true }),
        }
      : config.hullIsBasic,
  ]);

  // Hull <-> Hull-Basic conversion: cancelation data of the converted premium; the document date is
  // always shown, the reason only where the fleet's country uses it (not for AT)
  const { all: cancelPreviousPremiumFields } = useFields([
    config.cancelationDocumentDate,
    ...(appSettings?.HAS_REASON_FOR_CANCELLING_INSURANCE_PREMIUM_FIELD
      ? [config.reasonForCancellingInsurance]
      : []),
  ]);

  const fieldsForBottomSection = [];
  if (appSettings?.HAS_REASON_FOR_CONCLUDING_INSURANCE_PREMIUM_FIELD) {
    fieldsForBottomSection.push(config.reasonForConcludingInsurance);
  }
  fieldsForBottomSection.push(config.reasonForModificationComment);

  const { all: bottomFields } = useFields(fieldsForBottomSection);

  const { all: hullDeductibleFields } = useFields([
    ...(appSettings?.SHOW_HULL_PRODUCT ? [config.hullProduct] : []),
    config.gap,
    config.replacementValue,
    config.grossNegligence,
    config.electroPlusPackage,
    config.hullDeductiblePercent,
    config.hullDeductibleMinAmount,
    config.hullDeductibleMaxAmount,
    config.hullDeductibleComment,
    ...(appSettings?.HAS_HULL_ADDITIONAL_COVER
      ? [config.hullAdditionalCover]
      : []),
  ]);

  let left = (
    <>
      {premiumTypeKey === PremiumType.HULL ||
      premiumTypeKey === PremiumType.HULL_BASIC
        ? hullFields2
        : null}
      {premiumTypeKey === PremiumType.MTPL ||
      premiumTypeKey === PremiumType.HULL ||
      premiumTypeKey === PremiumType.HULL_BASIC
        ? mtplOrHullFieldsLeft
        : null}
      {leftFields}
    </>
  );
  let right = (
    <>
      {premiumTypeKey === PremiumType.MTPL ||
      premiumTypeKey === PremiumType.HULL ||
      premiumTypeKey === PremiumType.HULL_BASIC
        ? rightFieldsMtplOrHull
        : rightFieldsOther}
      {rightFields}
      {premiumTypeKey === PremiumType.HULL ||
      premiumTypeKey === PremiumType.HULL_BASIC
        ? hullFields1
        : null}
    </>
  );
  let bottom = <>{bottomFields}</>;

  if (
    premiumTypeKey === PremiumType.MTPL &&
    !vehicle.isTradeLicensePlate &&
    appSettings?.VEHICLE_PREMIUM_DOMAINS.includes(PremiumType.HORSE_POWER)
  ) {
    const mtplLeft = <>{motorTaxWithMtplLeft}</>;
    const mtplRight = <>{motorTaxWithMtplRight}</>;
    const mtplBottom = <>{motorTaxWithMtplBottom}</>;
    const mtplDialogContents = (
      <div className="row">
        <div className="column">
          {mtplLeft}
          {mtplRight}
          {mtplBottom}
        </div>
      </div>
    );

    return (
      <>
        <div className="row">
          <div className="column">{left}</div>
          <div className="column">{right}</div>
          {premiumTypeKey === PremiumType.MTPL ? (
            <>
              <div
                className="column"
                style={{ flex: "0.1%", backgroundColor: "#cccccc" }}
              ></div>
              <div className="column">{mtplDialogContents}</div>
            </>
          ) : null}
        </div>
        <div className="row">{bottom}</div>
      </>
    );
  }

  if (
    premiumTypeKey === PremiumType.HULL ||
    premiumTypeKey === PremiumType.HULL_BASIC
  ) {
    return (
      <>
        <div className="row">
          <div className="column">{left}</div>
          <div className="column">{right}</div>
          <>
            <div
              className="column"
              style={{ flex: "0.1%", backgroundColor: "#cccccc" }}
            ></div>
            <div className="column">{hullDeductibleFields}</div>
          </>
        </div>
        <div className="row">{bottom}</div>
        {isSwitchOperation && (
          <>
            <div className="row">
              <Label>
                {t("bfm.switchHullType.cancelPreviousPremium.label")}
              </Label>
            </div>
            <div className="row">{cancelPreviousPremiumFields}</div>
          </>
        )}
      </>
    );
  }

  if (
    premiumTypeKey === PremiumType.FLEETLEGAL_CLOSINGDATE ||
    premiumTypeKey === PremiumType.FLEETLEGAL_PRORATA
  ) {
    return <FleetLegalFormContent />;
  }

  if (
    premiumTypeKey === PremiumType.HULL_ON_LAND_CLOSINGDATE ||
    premiumTypeKey === PremiumType.HULL_ON_LAND_PRORATA
  ) {
    return <HullOnLandFormContent />;
  }
  if (
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_CLOSINGDATE ||
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_PRORATA
  ) {
    return <MachineryBreakdownFormContent />;
  }

  //     hullProduct
  // hullDeductible
  // hullDeductiblePercent
  // hullDeductibleMaxAmount
  // hullDeductibleMinAmount
  // hullDeductibleComment
  // gap
  // replacementValue
  // grossNegligence
  // electroPlusPackage

  return (
    <>
      <div className="row">
        <div className="column">{left}</div>
        <div className="column">{right}</div>
      </div>
      <div className="row">{bottom}</div>
    </>
  );
};
